import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  Chip,
  Stack,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  ThemeProvider,
  createTheme
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Launch as LaunchIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import AbsenceForm from '../absences/AbsenceForm';
import { useAbsences } from '../../contexts/AbsencesContext';

const WeekComplianceDialog = ({
  open,
  onClose,
  weekCompliance,
  technicians,
  skills,
  selectedYear,
  onNavigateToOnCall
}) => {
  const navigate = useNavigate();
  const { absences, loadAbsences } = useAbsences();
  
  // States for absence modal
  const [absenceModal, setAbsenceModal] = useState({
    open: false,
    absence: null,
    technicianId: null
  });

  if (!weekCompliance) return null;

  // Specialized theme for forcing z-index of menus above the modal
  const menuTheme = createTheme({
    components: {
      MuiPopover: { styleOverrides: { root: { zIndex: '1500 !important' } } },
      MuiMenu: { styleOverrides: { root: { zIndex: '1500 !important' } } },
      MuiModal: { styleOverrides: { root: { zIndex: '1500 !important' } } },
      MuiSelect: { styleOverrides: { root: { '& .MuiPopover-root': { zIndex: '1500 !important' } } } }
    }
  });

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'critical':
        return <ErrorIcon color="error" fontSize="small" />;
      case 'warning':
        return <WarningIcon color="warning" fontSize="small" />;
      default:
        return <CheckCircleIcon color="success" fontSize="small" />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'success';
    }
  };

  // Open absence modal over compliance dialog
  const handleNavigateToAbsence = (absenceId, technicianId) => {
    const absenceToEdit = absences.find(abs => abs.id === absenceId);
    if (absenceToEdit) {
      setAbsenceModal({
        open: true,
        absence: absenceToEdit,
        technicianId: technicianId
      });
    }
  };

  // Navigation to problematic on-call or week in planning
  const handleNavigateToOnCall = (onCallId, weekStart = null) => {
    if (onNavigateToOnCall) {
      onNavigateToOnCall(onCallId, weekStart);
    }
    onClose(); // Close dialog
  };

  // Absence modal handlers
  const handleCloseAbsenceModal = () => {
    setAbsenceModal({ open: false, absence: null, technicianId: null });
  };

  const handleAbsenceSuccess = () => {
    handleCloseAbsenceModal();
    loadAbsences(true); // Reload absences to update data
  };

  const renderResultItem = (item, index) => {
    const technician = item.technicianId ? technicians[item.technicianId] : null;
    const skill = item.skillId ? skills.find(s => s.id === item.skillId) : null;

    return (
      <Card key={index} sx={{ mb: 2, border: `1px solid ${getSeverityColor(item.severity)}.main` }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            {getSeverityIcon(item.severity)}
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.primary" sx={{ fontWeight: 'medium' }}>
                {item.message}
              </Typography>
              
              {item.details && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {item.details}
                </Typography>
              )}

              {/* Special display for rest period violations */}
              {item.type === 'REST_PERIOD_VIOLATION' && (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    📅 Fin astreinte: {item.currentEndDate} → Début suivante: {item.nextStartDate}
                  </Typography>
                  {technician && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      👤 {technician.firstName} {technician.lastName}
                    </Typography>
                  )}
                </>
              )}

              {/* Normal display for other types */}
              {item.type !== 'REST_PERIOD_VIOLATION' && technician && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  👤 {technician.firstName} {technician.lastName}
                </Typography>
              )}

              {skill && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  🎯 {skill.name}
                </Typography>
              )}

              {/* Affichage pour conflits d'absence groupés */}
              {item.type === 'ABSENCE_CONFLICT_GROUP' && item.absences && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  📋 Types d'absences: {item.absences.map(abs => {
                    // Fonction locale pour obtenir le label d'affichage
                    const ABSENCE_TYPES = {
                      CP: 'CA', JTL: 'JTL', MALADIE: 'Arrêt maladie', FORMATION: 'Formation',
                      CM: 'CM', TPS: 'Temps partiel', RECUP: 'Récupération', 
                      RECUP_ASTREINTE: 'Récup astreinte', MEDICAL: 'Visite médicale', AUTRE: 'Autre'
                    };
                    return ABSENCE_TYPES[abs.type?.toUpperCase()] || abs.type;
                  }).join(', ')}
                  {item.bypassActivated && ' (BYPASS ACTIVÉ)'}
                </Typography>
              )}

              {/* Affichage pour conflits d'absence individuels (rétrocompatibilité) */}
              {item.type === 'ABSENCE_CONFLICT' && item.absenceType && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  📋 Type d'absence: {item.absenceDisplayLabel || item.absenceType}
                  {item.bypassActivated && ' (BYPASS ACTIVÉ)'}
                  {!item.bypassActivated && ['JTL', 'RECUP_ASTREINTE'].includes(item.absenceType?.toUpperCase()) && ' (Compatible)'}
                  {!item.bypassActivated && ['CA', 'CP', 'ARRET MALADIE', 'MALADIE', 'FORMATION', 'TPS', 'CM', 'RECUP', 'MEDICAL'].includes(item.absenceType?.toUpperCase()) && ' (BLOQUANT)'}
                  {!item.bypassActivated && item.absenceType?.toUpperCase() === 'AUTRE' && ' (À corriger)'}
                </Typography>
              )}
            </Box>
            
            {/* Action buttons for navigation */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
              {/* Button to edit absence - individual conflict */}
              {item.type === 'ABSENCE_CONFLICT' && item.absenceId && (
                <Tooltip title="Éditer cette absence">
                  <IconButton 
                    size="small" 
                    color="primary" 
                    onClick={() => handleNavigateToAbsence(item.absenceId, item.technicianId)} 
                    sx={{ border: '1px solid', borderColor: 'primary.main' }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              
              {/* Buttons to edit absences - grouped conflict */}
              {item.type === 'ABSENCE_CONFLICT_GROUP' && item.absenceIds && item.absenceIds.length > 0 && (
                <>
                  {item.absenceIds.slice(0, 3).map((absenceId, idx) => (
                    <Tooltip key={idx} title={`Éditer absence ${idx + 1}`}>
                      <IconButton 
                        size="small" 
                        color="primary" 
                        onClick={() => handleNavigateToAbsence(absenceId, item.technicianId)} 
                        sx={{ border: '1px solid', borderColor: 'primary.main' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  ))}
                  {item.absenceIds.length > 3 && (
                    <Tooltip title={`+${item.absenceIds.length - 3} autres absences`}>
                      <Typography variant="caption" sx={{ alignSelf: 'center', color: 'text.secondary' }}>
                        +{item.absenceIds.length - 3}
                      </Typography>
                    </Tooltip>
                  )}
                </>
              )}
              
              {/* Buttons for rest period violations */}
              {item.type === 'REST_PERIOD_VIOLATION' && item.currentOnCallId && (
                <>
                  <Tooltip title="Voir la première astreinte">
                    <IconButton 
                      size="small" 
                      color="warning" 
                      onClick={() => handleNavigateToOnCall(item.currentOnCallId, null)} 
                      sx={{ border: '1px solid', borderColor: 'warning.main' }}
                    >
                      <LaunchIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {item.nextOnCallId && (
                    <Tooltip title="Voir la deuxième astreinte">
                      <IconButton 
                        size="small" 
                        color="error" 
                        onClick={() => handleNavigateToOnCall(item.nextOnCallId, null)} 
                        sx={{ border: '1px solid', borderColor: 'error.main' }}
                      >
                        <LaunchIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
              
              {/* Button to go to on-call or week (other types) */}
              {item.type !== 'REST_PERIOD_VIOLATION' && (item.onCallId || item.weekStart) && (
                <Tooltip title={item.onCallId ? "Voir cette astreinte dans le planning" : "Voir cette semaine dans le planning"}>
                  <IconButton 
                    size="small" 
                    color="secondary" 
                    onClick={() => handleNavigateToOnCall(item.onCallId, item.weekStart)} 
                    sx={{ border: '1px solid', borderColor: 'secondary.main' }}
                  >
                    <LaunchIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  const getStatusIcon = () => {
    switch (weekCompliance.status) {
      case 'valid':
        return <CheckCircleIcon color="success" sx={{ mr: 1 }} />;
      case 'warning':
        return <WarningIcon color="warning" sx={{ mr: 1 }} />;
      case 'error':
        return <ErrorIcon color="error" sx={{ mr: 1 }} />;
      default:
        return null;
    }
  };

  const getStatusMessage = () => {
    switch (weekCompliance.status) {
      case 'valid':
        return 'Aucun problème détecté';
      case 'warning':
        return `${weekCompliance.summary.warningIssues} avertissement(s)`;
      case 'error':
        return `${weekCompliance.summary.criticalIssues} erreur(s) critique(s) et ${weekCompliance.summary.warningIssues} avertissement(s)`;
      default:
        return 'Statut inconnu';
    }
  };

  const allIssues = [...weekCompliance.conflicts, ...weekCompliance.warnings];

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {getStatusIcon()}
              <Typography variant="h6" component="span">
                Conformité - Semaine {weekCompliance.weekNumber}
              </Typography>
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant="subtitle2" color="text.secondary" component="div">
            {weekCompliance.weekPeriod} • {selectedYear}
          </Typography>
        </DialogTitle>
        
        <DialogContent>
          {/* Status summary */}
          <Alert 
            severity={weekCompliance.status === 'valid' ? 'success' : weekCompliance.status === 'warning' ? 'warning' : 'error'}
            sx={{ mb: 3 }}
          >
            <Typography variant="body2">
              {getStatusMessage()}
            </Typography>
          </Alert>

          {/* Issues breakdown */}
          {allIssues.length > 0 ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Problèmes détectés ({allIssues.length})
              </Typography>
              
              {/* Summary chips */}
              <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
                {weekCompliance.summary.criticalIssues > 0 && (
                  <Chip
                    icon={<ErrorIcon />}
                    label={`${weekCompliance.summary.criticalIssues} Erreur(s) critique(s)`}
                    color="error"
                    variant="outlined"
                  />
                )}
                {weekCompliance.summary.warningIssues > 0 && (
                  <Chip
                    icon={<WarningIcon />}
                    label={`${weekCompliance.summary.warningIssues} Avertissement(s)`}
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Stack>

              {/* Issue details */}
              <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                {allIssues.map((item, index) => renderResultItem(item, index))}
              </Box>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CheckCircleIcon color="success" sx={{ fontSize: 64, mb: 2 }} />
              <Typography variant="h6" color="success.main">
                Semaine conforme
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Aucun problème de conformité détecté pour cette période.
              </Typography>
            </Box>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={onClose}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Absence editing modal over compliance dialog */}
      <Dialog
        open={absenceModal.open}
        onClose={handleCloseAbsenceModal}
        maxWidth="md"
        fullWidth
        sx={{ zIndex: 1300 }} // Standard z-index for absence modal
      >
        <DialogTitle>
          Modifier l'absence
          <IconButton onClick={handleCloseAbsenceModal} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {absenceModal.absence && (
            <ThemeProvider theme={menuTheme}> {/* Apply theme for z-index */}
              <AbsenceForm
                absence={absenceModal.absence}
                onClose={handleCloseAbsenceModal}
                onSuccess={handleAbsenceSuccess}
                technicianId={absenceModal.technicianId}
              />
            </ThemeProvider>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WeekComplianceDialog;

